/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { lAC_GetBasicUserInfoAsync } from '../custom-actions/DataActionExtension.g';
import { ILAC_BasicUserInfo } from '../custom-actions/DataServiceEntities.g';
import { dedupeInFlight } from '../utils/inflight';

export interface IUserInfoResult {
    data?: ILAC_BasicUserInfo;
    isUnauthorized?: boolean;
    isForbidden?: boolean;
    hasError?: boolean;
}

export class GetUserInfoInput implements Msdyn365.IActionInput {
    public intuitDomeAccountId: string;
    public token: string;
    constructor(intuitDomeAccountId: string, token: string) {
        this.intuitDomeAccountId = intuitDomeAccountId;
        this.token = token;
    }

    public getCacheKey = () => `GETUSERINFO`;
    public getCacheObjectType = () => 'GETUSERINFO';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

/**
 * Shares a call already in flight for the same account and token; a retry on a refreshed token makes a new one.
 */
async function getUserInfo(input: GetUserInfoInput, ctx: Msdyn365.IActionContext): Promise<IUserInfoResult> {
    return dedupeInFlight(`GetUserInfo_${input.intuitDomeAccountId}_${input.token}`, async () => performGetUserInfo(input, ctx));
}

async function performGetUserInfo(input: GetUserInfoInput, ctx: Msdyn365.IActionContext): Promise<IUserInfoResult> {
    try {
        const result = await lAC_GetBasicUserInfoAsync(
            { callerContext: ctx },
            { IntuitDomeAccountId: input.intuitDomeAccountId, Token: input.token }
        );
        return { data: result ?? {} };
    } catch (error) {
        const err = error as { data?: { ErrorResourceId?: string } };
        const statusCode = err?.data?.ErrorResourceId;
        const isUnauthorized = statusCode === '401';
        const isForbidden = statusCode === '403';
        return { hasError: true, isUnauthorized, isForbidden };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IUserInfoResult>>getUserInfo,
    id: 'GetUserInfo'
});
